import { supabase } from './supabase';

/**
 * Who is actually on My AI CTO, and what they do with it.
 *
 * The population is `subscriber_mcp_activations`, not `subscribers`. Being on the
 * newsletter list is not membership of this service — cto-mcp refuses a token without
 * an activation row — so anyone absent here has no access, free or paid, and does not
 * belong on this page.
 *
 * Four reads stitched in JS rather than one view: activation, tier, connected clients,
 * and recent calls live in four tables with different write paths, and a view would
 * have to be migrated every time one of them gains a column.
 *
 * See supabase/migrations/20260903120000_subscriber_mcp_activations.sql and
 * 20260903130000_mcp_usage_events.sql.
 */

export type UsageOutcome = 'success' | 'error' | 'denied';

export interface CtoMemberRow {
  subscriberId: string;
  name: string | null;
  email: string;

  /** Activation currently in force. */
  activatedAt: string;
  /** Set means access was turned off; the row stays as the record that they once had it. */
  revokedAt: string | null;
  termsVersion: string;
  addendumVersion: string;
  /** NULL means the cto-mcp-activation automation never took them — safe to backfill. */
  automationEnrolledAt: string | null;

  /** Catalog name, or 'Free' when there is no membership row at all. */
  tierName: string;
  /** Same rule cto-mcp uses to register paid tools. Not merely "has a membership row". */
  paid: boolean;
  membershipStatus: string | null;
  currentPeriodEnd: string | null;
  /** false = a Stripe test-mode row, which grants nothing in production. */
  livemode: boolean | null;

  /** Assistants they have authorized, e.g. ['Cursor', 'Claude']. */
  clients: string[];
  /** Lifetime, from the rollup — exact, unlike anything derived from the event window. */
  totalCalls: number;
  lastConnectedAt: string | null;
  /** Exact, from the rollup. NULL means connected but never used. */
  lastToolAt: string | null;

  /** Best effort: only known if their last call is inside the event window below. */
  lastToolName: string | null;
  lastToolOutcome: UsageOutcome | null;
  callsLast7Days: number;
  /** Calendar month tool calls (excludes denied and cto_whoami). */
  callsThisMonth: number;
  /** Effective monthly cap. null = unlimited. */
  monthlyCallLimit: number | null;
  usageUnlimited: boolean;
}

/**
 * How many recent events to pull for tool names and the weekly count.
 *
 * Newest-first and unbounded by date, so the most recent call for an active member is
 * always in here. Someone whose last call falls outside the window loses only the tool
 * name — `lastToolAt` still comes from the rollup and stays exact.
 */
const EVENT_WINDOW = 1000;

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Copied deliberately from cto-mcp/src/membership.ts rather than approximated. An admin
 * page that disagrees with the thing handing out tools is worse than no page: it would
 * have you debugging Stripe while the connector behaves correctly.
 *
 * past_due entitles because Stripe retries a failed card for days, and the grace window
 * covers a renewal webhook that arrives late.
 */
const ENTITLING_STATUSES = new Set(['active', 'trialing', 'past_due']);
const WEBHOOK_GRACE_MS = 3 * 24 * 60 * 60 * 1000;

function grantsPaidAccess(
  status: string,
  currentPeriodEnd: string | null,
  isFreeTier: boolean,
  now = Date.now(),
): boolean {
  if (!ENTITLING_STATUSES.has(status)) return false;
  if (isFreeTier) return false;
  if (!currentPeriodEnd) return true;

  const paidThrough = Date.parse(currentPeriodEnd);
  if (Number.isNaN(paidThrough)) return false;
  return now <= paidThrough + WEBHOOK_GRACE_MS;
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** bigint columns arrive as strings over PostgREST. */
function count(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

type MembershipInfo = {
  tierName: string;
  paid: boolean;
  status: string;
  currentPeriodEnd: string | null;
  livemode: boolean;
  usageUnlimited: boolean;
  monthlyCallLimitOverride: number | null;
  usageBonusCalls: number;
  usageBonusMonth: string | null;
  tierMonthlyCallLimit: number | null;
};

type ClientInfo = {
  clients: string[];
  totalCalls: number;
  lastConnectedAt: string | null;
  lastToolAt: string | null;
};

type UsageInfo = {
  lastToolName: string;
  lastToolOutcome: UsageOutcome;
  callsLast7Days: number;
  callsThisMonth: number;
};

function monthStartIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}

function monthStartDate(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

function effectiveLimit(info: MembershipInfo | null): {
  limit: number | null;
  unlimited: boolean;
} {
  if (!info) {
    return { limit: null, unlimited: false };
  }
  if (info.usageUnlimited) return { limit: null, unlimited: true };
  const bonus =
    info.usageBonusMonth === monthStartDate() ? Math.max(0, info.usageBonusCalls) : 0;
  if (info.monthlyCallLimitOverride != null) {
    return { limit: info.monthlyCallLimitOverride + bonus, unlimited: false };
  }
  if (info.tierMonthlyCallLimit == null) {
    return { limit: null, unlimited: true };
  }
  return { limit: info.tierMonthlyCallLimit + bonus, unlimited: false };
}

/**
 * Both Stripe modes share this database. A live row wins, because that is the one that
 * grants real access; a test row is kept only so the table can label it as such and
 * explain why someone "on Premium" has no paid tools.
 */
function pickMembership(rows: MembershipInfo[]): MembershipInfo | null {
  if (rows.length === 0) return null;
  return rows.find((r) => r.livemode) ?? rows[0];
}

export async function fetchCtoMembers(): Promise<CtoMemberRow[]> {
  const [activationsRes, membershipsRes, clientsRes, eventsRes] = await Promise.all([
    supabase
      .from('subscriber_mcp_activations')
      .select(
        'subscriber_id, activated_at, revoked_at, terms_version, addendum_version, automation_enrolled_at, subscriber:subscribers(name, email)',
      )
      .order('activated_at', { ascending: false }),
    supabase
      .from('subscriber_memberships')
      .select(
        'subscriber_id, livemode, status, current_period_end, monthly_call_limit_override, usage_unlimited, usage_bonus_calls, usage_bonus_month, tier:membership_tiers(name, is_free, monthly_call_limit)',
      ),
    supabase
      .from('subscriber_mcp_clients')
      .select('subscriber_id, client_name, last_connected_at, last_tool_call_at, tool_call_count'),
    supabase
      .from('mcp_usage_events')
      .select('subscriber_id, occurred_at, tool_name, outcome')
      .order('occurred_at', { ascending: false })
      .limit(EVENT_WINDOW),
  ]);

  if (activationsRes.error) throw new Error(activationsRes.error.message);
  if (membershipsRes.error) throw new Error(membershipsRes.error.message);
  if (clientsRes.error) throw new Error(clientsRes.error.message);
  if (eventsRes.error) throw new Error(eventsRes.error.message);

  // ---- memberships, grouped so a live row can outrank a test one
  const membershipsBySubscriber = new Map<string, MembershipInfo[]>();
  for (const raw of membershipsRes.data ?? []) {
    const row = raw as Record<string, unknown>;
    const tier = (row.tier ?? null) as {
      name?: unknown;
      is_free?: unknown;
      monthly_call_limit?: unknown;
    } | null;
    const status = String(row.status ?? '');
    const currentPeriodEnd = str(row.current_period_end);
    const isFree = Boolean(tier?.is_free);
    const id = String(row.subscriber_id);

    const list = membershipsBySubscriber.get(id) ?? [];
    list.push({
      tierName: String(tier?.name ?? 'Unknown tier'),
      paid: grantsPaidAccess(status, currentPeriodEnd, isFree),
      status,
      currentPeriodEnd,
      livemode: Boolean(row.livemode),
      usageUnlimited: Boolean(row.usage_unlimited),
      monthlyCallLimitOverride:
        row.monthly_call_limit_override == null
          ? null
          : Number(row.monthly_call_limit_override),
      usageBonusCalls: count(row.usage_bonus_calls),
      usageBonusMonth: str(row.usage_bonus_month),
      tierMonthlyCallLimit:
        tier?.monthly_call_limit == null ? null : Number(tier.monthly_call_limit),
    });
    membershipsBySubscriber.set(id, list);
  }

  // ---- connected clients and the exact lifetime rollup
  const clientsBySubscriber = new Map<string, ClientInfo>();
  for (const raw of clientsRes.data ?? []) {
    const row = raw as Record<string, unknown>;
    const id = String(row.subscriber_id);
    const existing = clientsBySubscriber.get(id) ?? {
      clients: [],
      totalCalls: 0,
      lastConnectedAt: null,
      lastToolAt: null,
    };

    const clientName = String(row.client_name ?? '').trim();
    if (clientName) existing.clients.push(clientName);
    existing.totalCalls += count(row.tool_call_count);
    existing.lastConnectedAt = laterOf(existing.lastConnectedAt, str(row.last_connected_at));
    existing.lastToolAt = laterOf(existing.lastToolAt, str(row.last_tool_call_at));

    clientsBySubscriber.set(id, existing);
  }

  // ---- recent events: newest first, so the first hit per subscriber is their last call
  const weekAgo = Date.now() - WEEK_MS;
  const monthStart = Date.parse(monthStartIso());
  const usageBySubscriber = new Map<string, UsageInfo>();
  for (const raw of eventsRes.data ?? []) {
    const row = raw as Record<string, unknown>;
    const id = String(row.subscriber_id);
    const occurredAt = str(row.occurred_at);
    const outcome = String(row.outcome ?? 'success') as UsageOutcome;
    const toolName = String(row.tool_name ?? '');

    let entry = usageBySubscriber.get(id);
    if (!entry) {
      entry = {
        lastToolName: String(row.tool_name ?? ''),
        lastToolOutcome: outcome,
        callsLast7Days: 0,
        callsThisMonth: 0,
      };
      usageBySubscriber.set(id, entry);
    }

    // Denied calls never ran, so they are not usage and must not inflate the count.
    if (outcome !== 'denied' && occurredAt && Date.parse(occurredAt) >= weekAgo) {
      entry.callsLast7Days += 1;
    }
    if (
      outcome !== 'denied' &&
      toolName !== 'cto_whoami' &&
      occurredAt &&
      Date.parse(occurredAt) >= monthStart
    ) {
      entry.callsThisMonth += 1;
    }
  }

  return (activationsRes.data ?? []).map((raw) => {
    const row = raw as Record<string, unknown>;
    const subscriber = (row.subscriber ?? null) as { name?: unknown; email?: unknown } | null;
    const id = String(row.subscriber_id);

    const membership = pickMembership(membershipsBySubscriber.get(id) ?? []);
    const clientInfo = clientsBySubscriber.get(id);
    const usage = usageBySubscriber.get(id);
    const limitInfo = effectiveLimit(membership);

    return {
      subscriberId: id,
      name: str(subscriber?.name),
      email: String(subscriber?.email ?? ''),

      activatedAt: String(row.activated_at),
      revokedAt: str(row.revoked_at),
      termsVersion: String(row.terms_version ?? ''),
      addendumVersion: String(row.addendum_version ?? ''),
      automationEnrolledAt: str(row.automation_enrolled_at),

      // No membership row is the normal state for a free member, not missing data.
      tierName: membership?.tierName ?? 'Free',
      paid: membership?.paid ?? false,
      membershipStatus: membership?.status ?? null,
      currentPeriodEnd: membership?.currentPeriodEnd ?? null,
      livemode: membership?.livemode ?? null,

      clients: clientInfo?.clients ?? [],
      totalCalls: clientInfo?.totalCalls ?? 0,
      lastConnectedAt: clientInfo?.lastConnectedAt ?? null,
      lastToolAt: clientInfo?.lastToolAt ?? null,

      lastToolName: usage?.lastToolName ?? null,
      lastToolOutcome: usage?.lastToolOutcome ?? null,
      callsLast7Days: usage?.callsLast7Days ?? 0,
      callsThisMonth: usage?.callsThisMonth ?? 0,
      monthlyCallLimit: limitInfo.limit,
      usageUnlimited: limitInfo.unlimited,
    };
  });
}

/** One MCP call, as the connector recorded it. */
export interface McpActivityRow {
  id: string;
  occurredAt: string;
  clientName: string;
  toolName: string;
  tier: string;
  outcome: UsageOutcome;
  durationMs: number | null;
  errorCode: string | null;
  /** MCP transport hookup. Same value means the same live connector, not the same chat. */
  connectionId: string | null;
  /** What was asked. Credential-shaped keys and oversized values are cut by the Worker. */
  requestArgs: Record<string, unknown> | null;
  /** The opening of what came back, truncated. Never the whole response. */
  responseSummary: string | null;
}

export type ActivityConnection = {
  connectionId: string | null;
  calls: McpActivityRow[];
};

/** Newest connection first. Rows with no id stay in one ungrouped bucket. */
export function groupActivityByConnection(rows: McpActivityRow[]): ActivityConnection[] {
  const order: string[] = [];
  const by = new Map<string, McpActivityRow[]>();
  for (const row of rows) {
    const key = row.connectionId ?? '';
    if (!by.has(key)) {
      by.set(key, []);
      order.push(key);
    }
    by.get(key)!.push(row);
  }
  return order.map((key) => ({
    connectionId: key || null,
    calls: by.get(key) ?? [],
  }));
}

/**
 * The call log for one member.
 *
 * Which tool, from which assistant, how it ended, how long it took, what was asked, and the
 * opening of what came back. The response is a truncated summary rather than the whole
 * thing: a full cto_code_review reply describes a customer's private codebase, and keeping
 * every one of them would make this table a copy of their source.
 */
export async function fetchMemberActivity(
  subscriberId: string,
  limit = 200,
): Promise<McpActivityRow[]> {
  const { data, error } = await supabase
    .from('mcp_usage_events')
    .select(
      'id, occurred_at, client_name, tool_name, tier, outcome, duration_ms, error_code, request_args, response_summary, connection_id',
    )
    .eq('subscriber_id', subscriberId)
    .order('occurred_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);

  return (data ?? []).map((raw) => {
    const row = raw as Record<string, unknown>;
    return {
      id: String(row.id),
      occurredAt: String(row.occurred_at),
      clientName: String(row.client_name ?? ''),
      toolName: String(row.tool_name ?? ''),
      tier: String(row.tier ?? ''),
      outcome: (row.outcome as UsageOutcome) ?? 'success',
      durationMs: row.duration_ms === null || row.duration_ms === undefined
        ? null
        : Number(row.duration_ms),
      errorCode: str(row.error_code),
      connectionId: str(row.connection_id),
      requestArgs:
        row.request_args && typeof row.request_args === 'object'
          ? (row.request_args as Record<string, unknown>)
          : null,
      responseSummary: str(row.response_summary),
    };
  });
}

function laterOf(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

/** `cto_code_review` is the wire name; "Code review" is what it is called out loud. */
export function formatToolName(toolName: string | null): string {
  if (!toolName) return '—';
  const bare = toolName.replace(/^cto_/, '').replace(/_/g, ' ');
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}

/** Coarse on purpose — an exact timestamp invites reading precision that isn't there. */
export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';

  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  return new Date(then).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  return new Date(then).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The one-line diagnosis for a row, in the order that matters operationally. Activated
 * and never connected is a different failure from connected and never used, and both
 * are worth acting on before anyone thinks about upselling.
 */
export function memberState(
  row: CtoMemberRow,
): { label: string; tone: 'good' | 'warn' | 'idle' | 'off' } {
  if (row.revokedAt) return { label: 'Revoked', tone: 'off' };
  if (row.clients.length === 0) return { label: 'Not connected', tone: 'warn' };
  if (!row.lastToolAt) return { label: 'Never used', tone: 'warn' };
  if (row.callsLast7Days > 0) return { label: 'Active', tone: 'good' };
  return { label: 'Idle', tone: 'idle' };
}
